import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import { useOrganization } from '@/contexts/OrganizationContext';
import { authedDelete, authedPatch, authedPost } from '@/lib/api';
import { BudgetCategoryGroup } from '@/types/budgets';

export const useBudgetCategoryGroups = (budgetId?: string) => {
  return useQuery({
    queryKey: ['budgetCategoryGroups', budgetId],
    queryFn: async () => {
      if (!budgetId) return [];

      const { data, error } = await supabase
        .from('budget_category_groups')
        .select('*')
        .eq('budget_id', budgetId)
        .order('sort_order');

      if (error) throw error;
      return data as BudgetCategoryGroup[];
    },
    enabled: !!budgetId,
  });
};

// Writes go through the Hono backend (Pattern B, /api/v1/budgets — see
// apps/api/src/routes/accounting/budgets.ts); the read above stays direct for
// now. Also reached by the EditableTable inline group create
// (`_create:<name>` → useResolveGroupId → useCreateCategoryGroup).
const groupsPath = (organizationId: string, budgetId: string) =>
  `/api/v1/budgets/orgs/${organizationId}/budgets/${budgetId}/groups`;

export const useCreateCategoryGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      budget_id: string;
      organization_id: string;
      name: string;
      is_income?: boolean;
      sort_order?: number;
    }) => {
      // organization_id / budget_id travel in the URL, never the body.
      const { budget_id, organization_id, ...body } = data;
      return authedPost<BudgetCategoryGroup>(groupsPath(organization_id, budget_id), body);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['budgetCategoryGroups', result.budget_id] });
      queryClient.invalidateQueries({ queryKey: ['budgetMonthView', result.budget_id] });
      queryClient.invalidateQueries({ queryKey: ['budgetTotalView', result.budget_id] });
      // No success toast — batch saves handle their own feedback
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};

/** The org comes from OrganizationContext; `budgetId` scopes the API path. */
export const useUpdateCategoryGroup = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({ id, budgetId, data }: {
      id: string;
      budgetId: string;
      data: Partial<Pick<BudgetCategoryGroup, 'name' | 'sort_order' | 'is_hidden' | 'is_income'>>;
    }) => {
      if (!selectedOrganization?.id) throw new Error('No organization selected');
      return authedPatch<BudgetCategoryGroup>(`${groupsPath(selectedOrganization.id, budgetId)}/${id}`, data);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['budgetCategoryGroups', result.budget_id] });
      queryClient.invalidateQueries({ queryKey: ['budgetMonthView', result.budget_id] });
      queryClient.invalidateQueries({ queryKey: ['budgetTotalView', result.budget_id] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};

export const useDeleteCategoryGroup = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({ id, budgetId }: { id: string; budgetId: string }) => {
      if (!selectedOrganization?.id) throw new Error('No organization selected');
      await authedDelete<{ deleted: number }>(`${groupsPath(selectedOrganization.id, budgetId)}/${id}`);
      return { budgetId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['budgetCategoryGroups', result.budgetId] });
      queryClient.invalidateQueries({ queryKey: ['budgetMonthView', result.budgetId] });
      queryClient.invalidateQueries({ queryKey: ['budgetTotalView', result.budgetId] });
      // No success toast — batch saves handle their own feedback
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};
